/**
 * jobStatusText.js
 *
 * Returns human-readable status text from row data (JobEditorTable rows)
 * based on the preferred viewer type.
 *
 * Row shapes handled:
 *  - Parent row (_isCombined: true)  → aggregates across all required quotes
 *  - Child row  (_isAccordionChild)  → reads from _quoteData (camelCase keys)
 *
 * Viewer types:
 *  - "editor"     → estimation_status, design_status
 *  - "delivery"   → estimation_status, design_status, production_status, delivery_status
 *  - "assignment" → estimation_status, design_status, production_status, delivery_status
 *  - "production" → production_status
 *  - "schedule"   → design_status (only for the matching quote_type)
 */

// ─── Label maps ──────────────────────────────────────────────────────────────

const DESIGN_STATUS_TEXT = {
    not_required: 'Waiting Assignment',
    waiting_assignment: 'Waiting Assignment',
    waiting_design: 'Waiting Design',
    in_progress: 'In Design',
    completed: 'Completed',
};

const ESTIMATION_STATUS_TEXT = {
    not_required: 'Not Required',
    waiting_assignment: 'Waiting Assignment',
    waiting_design: 'Waiting Assignment',
    in_progress: 'In Estimation',
    completed: 'Completed',
};

const PRODUCTION_STATUS_TEXT = {
    not_required: 'Not Required',
    not_started: 'Not Started',
    in_cutting: 'In Cutting',
    cutting_ended: 'Cutting Finished',
    in_production: 'In Production',
    completed: 'Completed',
};

const DELIVERY_STATUS_TEXT = {
    not_scheduled: 'Not Scheduled',
    scheduled: 'Scheduled',
    in_transit: 'In Transit',
    delivered: 'Delivered',
    cancelled: 'Cancelled',
};


const FILE_ONLY_QUOTE_TYPES = new Set(["beams", "flooring_sheets"]);
const isFileOnlyQuoteType = (quoteType) => FILE_ONLY_QUOTE_TYPES.has(quoteType);

// ─── Single-field helpers ─────────────────────────────────────────────────────

/** @param {string} status @returns {string} */
export const getDesignStatusText = (status) =>
    DESIGN_STATUS_TEXT[status] ?? status ?? 'Unknown';

/** @param {string} status @returns {string} */
export const getEstimationStatusText = (status) =>
    ESTIMATION_STATUS_TEXT[status] ?? status ?? 'Unknown';

/** @param {string} status @returns {string} */
export const getProductionStatusText = (status) =>
    PRODUCTION_STATUS_TEXT[status] ?? status ?? 'Unknown';

/** @param {string} status @returns {string} */
export const getDeliveryStatusText = (status) =>
    DELIVERY_STATUS_TEXT[status] ?? status ?? 'Unknown';

// ─── Internal helpers ─────────────────────────────────────────────────────────

const QUOTE_TYPES = ['roof', 'wall', 'floor', 'timber'];

/**
 * Normalise a single quote object (handles both camelCase and snake_case keys).
 * @param {Object} q
 * @returns {{ designStatus, estimationStatus, productionStatus, deliveryStatus }}
 */
const normaliseQuote = (q = {}) => {
    const quoteType = q.quoteType ?? q.quote_type;

    return {
        quoteType,
        designStatus: q.designStatus ?? q.design_status,
        estimationStatus: q.estimationStatus ?? q.estimation_status,
        productionStatus: isFileOnlyQuoteType(quoteType)
            ? "not_required"
            : (q.productionStatus ?? q.production_status),
        deliveryStatus: q.deliveryStatus ?? q.delivery_status,
    };
};
/**
 * Priority order used when aggregating across multiple quotes.
 * Earlier = higher priority (shown first if mixed).
 */
const STATUS_PRIORITY = [
    'not_required',
    'in_progress',
    'waiting_assignment',
    'waiting_design',
    'not_started',
    'in_cutting',
    'cutting_ended',
    'in_production',
    'scheduled',
    'in_transit',
    'completed',
    'not_scheduled',
];

const priorityOf = (status) => {
    const idx = STATUS_PRIORITY.indexOf(status);
    return idx === -1 ? STATUS_PRIORITY.length : idx;
};

/**
 * Given an array of status values, return the one with the highest priority
 * (ignoring null / undefined / not_required).
 * Falls back to 'not_required' if all are not_required / missing.
 * @param {string[]} statuses
 * @returns {string}
 */
const aggregateStatus = (statuses) => {
    const valid = statuses.filter(s => s && s !== 'not_required');
    if (!valid.length) return 'not_required';
    return valid.reduce((best, cur) => priorityOf(cur) < priorityOf(best) ? cur : best);
};

/**
 * Extract a normalised quote object from a row for a given viewer type.
 *
 * - Child row  (_isAccordionChild / _quoteData present) → use _quoteData directly
 * - Parent row (_isCombined)                            → aggregate across quotes.*
 *
 * @param {Object} row - row.original from the table
 * @returns {{ designStatus, estimationStatus, productionStatus, deliveryStatus }}
 */
const resolveQuoteFromRow = (row) => {
    // ── Child row ─────────────────────────────────────────────────────────────
    if (row._quoteData || row.quote_data || row.quote_info) {
        return normaliseQuote(row._quoteData || row.quote_data || row.quote_info);
    }

    let requiredQuotes = [];

    // ── Parent row — works for both UUID-keyed (new) and type-keyed (old) quotes
    if (row.quotes) {
        requiredQuotes = Object.values(row.quotes)
            .filter(q => q?.isRequired || q?.is_required)
            .map(normaliseQuote);
    }

    // ── Parent row (array-based quotes: row.job_quotes[]) ─────────────────────
    if ((!requiredQuotes.length) && Array.isArray(row.job_quotes)) {
        requiredQuotes = row.job_quotes
            .filter(q => q?.is_required || q?.isRequired)
            .map(normaliseQuote);
    }

    if (!requiredQuotes.length) return normaliseQuote({});

    return {
        designStatus: aggregateStatus(requiredQuotes.map(q => q.designStatus)),
        estimationStatus: aggregateStatus(requiredQuotes.map(q => q.estimationStatus)),
        productionStatus: aggregateStatus(requiredQuotes.map(q => q.productionStatus)),
        deliveryStatus: aggregateStatus(requiredQuotes.map(q => q.deliveryStatus)),
    };
};

// ─── Field definitions ────────────────────────────────────────────────────────

/**
 * All available status field keys and how to resolve them.
 */
const FIELD_MAP = {
    estimation: (q, row) => {
        const estimator = row?.job_estimators || row?.estimator;
        let status = q.estimationStatus;

        if (estimator) {
            if (estimator.is_completed === true) {
                status = 'completed';
            } else if (estimator.is_completed === false) {
                status = 'in_progress';
            }
        }

        return {
            label: 'Estimation',
            key: 'estimation_status',
            status,
            text: getEstimationStatusText(status)
        };
    },
    design: (q) => ({ label: 'Design', key: 'design_status', status: q.designStatus, text: getDesignStatusText(q.designStatus) }),
    production: (q) => ({ label: 'Production', key: 'production_status', status: q.productionStatus, text: getProductionStatusText(q.productionStatus) }),
    delivery: (q) => ({ label: 'Delivery', key: 'delivery_status', status: q.deliveryStatus, text: getDeliveryStatusText(q.deliveryStatus) }),
};

const ALL_FIELDS = ['estimation', 'design', 'production', 'delivery'];

// ─── Preset resolvers ─────────────────────────────────────────────────────────

/**
 * 'editor' preset — smart sequential logic based on jobType.
 *
 * - Design only          → show design status
 * - Estimation only      → show estimation status
 * - Design & Estimation  → estimation first; once estimation is completed AND
 *                          design has moved past waiting_assignment, show design instead
 *
 * @param {Object} q        - resolved quote statuses
 * @param {string} jobType  - row.original.jobType
 * @returns {Array<{ label, key, status, text }>}
 */
const resolveEditorPreset = (q, jobType, quoteType) => {
    const type = (jobType ?? '').toLowerCase();
    const hasDesign = type.includes('design');
    const hasEstimation = type.includes('estimation');


    // Design only
    if (hasDesign && !hasEstimation) {
        return [FIELD_MAP.design(q)];
    }

    // Estimation only
    if (hasEstimation && !hasDesign) {
        return [FIELD_MAP.estimation(q)];
    }

    // Design & Estimation — sequential logic
    if (hasDesign && hasEstimation) {
        const estimationDone = q.estimationStatus === 'completed';
        const designProgressed = ['waiting_design', 'in_progress', 'completed'].includes(q.designStatus);

        if (estimationDone && designProgressed) {
            return [FIELD_MAP.design(q)];
        }
        return [FIELD_MAP.estimation(q)];
    }

    // Fallback — show both
    return [FIELD_MAP.estimation(q), FIELD_MAP.design(q)];
};

const PRESETS = {
    editor: resolveEditorPreset,
};

// ─── Main helpers ─────────────────────────────────────────────────────────────

/**
 * Returns an ordered array of { label, key, status, text } objects
 * for the requested field(s) or preset.
 *
 * @param {Object} row
 * @param {string|string[]|'all'} fields
 *   - Single key:   'estimation' | 'design' | 'production' | 'delivery'
 *   - Multiple keys: ['estimation', 'design']
 *   - All:          'all'
 *   - Preset:       'editor'  (uses jobType-aware logic)
 * @returns {Array<{ label: string, key: string, status: string, text: string }>}
 */
export const getJobStatusFields = (row, fields) => {
    const q = resolveQuoteFromRow(row);

    // Preset
    if (typeof fields === 'string' && PRESETS[fields]) {
        return PRESETS[fields](q, row.jobType ?? row.job_type);
    }

    // Single key, array of keys, or 'all'
    const keys = fields === 'all'
        ? ALL_FIELDS
        : Array.isArray(fields) ? fields : [fields];

    return keys
        .filter(k => FIELD_MAP[k])
        .map(k => FIELD_MAP[k](q, row));
};

/**
 * Returns a plain-text summary string for the requested field(s) or preset.
 *
 * @param {Object} row
 * @param {string|string[]|'all'} fields
 * @returns {string}
 *
 * @example
 * getJobStatusSummary(row, 'estimation')              // "In Progress"
 * getJobStatusSummary(row, ['estimation', 'design'])  // "In Progress | Completed"
 * getJobStatusSummary(row, 'editor')                  // jobType-aware: one field shown
 * getJobStatusSummary(row, 'all')                     // all four fields
 */
export const getJobStatusSummary = (row, fields) => {
    const result = getJobStatusFields(row, fields);
    if (!result.length) return '';
    return result.map(f => f.text).join(' | ');
};

// _-----------------------------------------------------------------------------
export const getJobStatusFieldsForQuotes = (row, quoteType, fields = 'all') => {
    const q = resolveQuoteFromRowByType(row, quoteType);

    // Preset (editor logic etc.)
    if (typeof fields === 'string' && PRESETS[fields]) {
        return PRESETS[fields](q, row.jobType ?? row.job_type, quoteType);
    }

    const keys = fields === 'all'
        ? ALL_FIELDS
        : Array.isArray(fields) ? fields : [fields];

    return keys
        .filter(k => FIELD_MAP[k])
        .map(k => FIELD_MAP[k](q, row));
};


const resolveQuoteFromRowByType = (row, quoteType) => {
    const quote = row?.job_quotes?.find(q => q.quote_type === quoteType);

    if (!quote) return normaliseQuote({});

    return normaliseQuote(quote);
};

export const getJobStatusSummarySingleQuote = (row, quoteType, fields = 'all') => {
    const result = getJobStatusFieldsForQuotes(row, quoteType, fields);
    if (!result.length) return '';
    return result.map(f => f.text).join(' | ');
};
